import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Highlighter, Plus, Download, Upload, Lock, Sparkles, Trash2, Crown } from "lucide-react";
import { contrastFor } from "./DocColorControls";
import { useEntitlements } from "../context/EntitlementsContext";

/**
 * HighlightRoster (v3 — iter28 redesign)
 *
 * One highlighter button in the toolbar that opens a unified menu:
 *   1. Roster toggle (real toggle switch, at top)
 *   2. Single color grid — doubles as roster binding grid when roster
 *      mode is on. Click/right-click binds L/R. When roster mode is
 *      OFF, clicking applies a one-shot highlight.
 *   3. Custom color lives as an inline "+"-glyph swatch in the grid.
 *   4. "No Highlight Color" button at the bottom.
 *
 * Editor behaviour when roster ON:
 *   - Left-drag → apply LEFT-bound color on mouseup (toggle if same)
 *   - Right-drag → apply RIGHT-bound color
 *   - Middle-drag → native selection, no color paint
 *   - Middle-hold + left-click → cycle LEFT binding across roster
 *   - Middle-hold + right-click → cycle RIGHT binding
 *   - Native selection (blue) is cleared after apply.
 *
 * Persistence: localStorage['cresc-doc-hl-roster'].
 * Feature-gate: the roster toggle is PRO-ONLY. Free/Standard users see
 * a small lock icon next to the toggle. One-shot highlighting always
 * works (no paywall on basic highlight).
 */

const PRESET_HIGHLIGHTS = [
  "#FFEB3B", "#FFC107", "#FF9800", "#FF5722", "#F44336",
  "#E91E63", "#9C27B0", "#673AB7", "#3F51B5", "#2196F3",
  "#03A9F4", "#00BCD4", "#009688", "#4CAF50", "#8BC34A",
  "#CDDC39", "#FFFFFF",
];

const DEFAULT_ROSTER = ["#FFEB3B", "#FF9800", "#F44336", "#4CAF50", "#03A9F4", "#9C27B0"];
const STORE_KEY = "cresc-doc-hl-roster";
const RECENT_KEY = "cresc-doc-recent-colors";


// ─── Brand roster (business accounts) ────────────────────────────────────────
// Shared roster stored on the server and synced to all business org members.
// Falls back to personal roster if not a business account.
const BRAND_STORE_KEY = "cresc-brand-hl-roster";

async function fetchBrandRoster(api) {
  try {
    const { data } = await api.get("/me/brand-roster");
    return data;
  } catch { return null; }
}

async function saveBrandRoster(api, payload) {
  try {
    await api.put("/me/brand-roster", payload);
  } catch {}
}

function loadStore() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || "null"); } catch { return null; }
}
function saveStore(state) { try { localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch {} }
function loadRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "{}").hl || []; } catch { return []; }
}
function saveRecent(color) {
  try {
    const all = JSON.parse(localStorage.getItem(RECENT_KEY) || "{}");
    const arr = all.hl || [];
    all.hl = [color, ...arr.filter((c) => c.toLowerCase() !== color.toLowerCase())].slice(0, 6);
    localStorage.setItem(RECENT_KEY, JSON.stringify(all));
  } catch {}
}

function toHex(input) {
  if (!input) return null;
  const s = String(input).trim().toLowerCase();
  if (s === "transparent" || s === "rgba(0, 0, 0, 0)") return "transparent";
  const hexMatch = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hexMatch) {
    const h = hexMatch[1];
    if (h.length === 3) return "#" + h.split("").map((c) => c + c).join("");
    return "#" + h;
  }
  const rgbMatch = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    return "#" + [1, 2, 3].map((i) => parseInt(rgbMatch[i], 10).toString(16).padStart(2, "0")).join("");
  }
  return null;
}
function colorEquals(a, b) {
  const ah = toHex(a); const bh = toHex(b);
  if (!ah || !bh) return false;
  return ah === bh;
}

/** Minimal iOS-style toggle switch (not a checkbox). */
function ToggleSwitch({ checked, onChange, disabled, "data-testid": tid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked ? "true" : "false"}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 rounded-full transition-all ${checked ? "bg-[#5A90F5]" : "bg-[#2A2A2A]"} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
      data-testid={tid}
    >
      <span className={`inline-block w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[18px]" : "translate-x-[2px]"} translate-y-[2px]`} />
    </button>
  );
}

export function HighlightRoster({ exec, userPickedTextColorRef, setLastForeColor, editorRef, "data-testid": tid = "docs-hl-roster", apiClient }) {
  const { features } = useEntitlements();
  const rosterAllowed = !!features?.highlight_roster;
  const isBusiness = !!features?.is_business_member;
  const [brandRoster, setBrandRoster] = useState(null); // null = not loaded yet
  const [showBrandRoster, setShowBrandRoster] = useState(false);
  const [brandSaving, setBrandSaving] = useState(false);

  const initial = loadStore();
  const [palette, setPalette] = useState(() => {
    // Merge saved custom colors from "roster" array into the main palette
    // so every user keeps their colors even if they never enable roster
    // mode. De-dupe against presets.
    const saved = initial?.palette;
    if (Array.isArray(saved) && saved.length) return saved;
    return PRESET_HIGHLIGHTS;
  });
  const [leftColor, setLeftColor] = useState(initial?.leftColor || null);
  const [rightColor, setRightColor] = useState(initial?.rightColor || null);
  const [rosterEnabled, setRosterEnabled] = useState(initial?.rosterEnabled && rosterAllowed);
  const [lastOneShot, setLastOneShot] = useState(initial?.lastOneShot || null);
  const [recents, setRecents] = useState(loadRecent());
  const [open, setOpen] = useState(false);
  const [customHex, setCustomHex] = useState("");
  const wrapRef = useRef(null);
  const [middleIndicator, setMiddleIndicator] = useState(null);
  // Iter39: right-click delete menu for swatches in roster mode.
  const [colorCtx, setColorCtx] = useState(null); // { color, x, y }

  // Iter39 user request: "When enabling the roster, the palette in the
  // highlight menu should switch. In this menu, there should only be
  // an option to add colors, where the user can add custom colors that
  // get used in the highlight tool." When roster mode is on we
  // suppress the preset bank and only show user-added customs.
  const PRESET_SET = useMemo(() => new Set(PRESET_HIGHLIGHTS.map((c) => c.toLowerCase())), []);
  const visiblePalette = rosterEnabled
    ? palette.filter((c) => !PRESET_SET.has((c || "").toLowerCase()))
    : palette;

  // Sync roster-enabled state across all instances (toolbar + context menu
  // each have their own local state, but they share localStorage).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("cresc-roster-sync", { detail: { rosterEnabled } }));
  }, [rosterEnabled]);

  useEffect(() => {
    const onSync = (e) => {
      if (e.detail?.rosterEnabled !== rosterEnabled) {
        setRosterEnabled(e.detail.rosterEnabled && rosterAllowed);
      }
    };
    window.addEventListener("cresc-roster-sync", onSync);
    return () => window.removeEventListener("cresc-roster-sync", onSync);
  }, [rosterEnabled, rosterAllowed]);

  const removeFromPalette = (c) => {
    const norm = (c || "").toLowerCase();
    setPalette((cur) => cur.filter((x) => (x || "").toLowerCase() !== norm));
    // Also drop active bindings if they pointed at the deleted color.
    if (leftColor && (leftColor || "").toLowerCase() === norm) setLeftColor(null);
    if (rightColor && (rightColor || "").toLowerCase() === norm) setRightColor(null);
    setColorCtx(null);
  };

  // Force roster off if the user downgrades plans.
  useEffect(() => { if (!rosterAllowed && rosterEnabled) setRosterEnabled(false); }, [rosterAllowed, rosterEnabled]);
  useEffect(() => { saveStore({ palette, leftColor, rightColor, rosterEnabled, lastOneShot }); }, [palette, leftColor, rightColor, rosterEnabled, lastOneShot]);

  // Iter34: global Alt+C toggle. Works only if the user has access to
  // the feature; otherwise fires the paywall modal.
  useEffect(() => {
    const onToggle = () => {
      if (!rosterAllowed) {
        window.dispatchEvent(new CustomEvent("crescendo:paywall", {
          detail: { error: "feature_locked", feature: "highlight_roster", plan: "standard",
                    message: "Highlight Roster is a Pro feature." },
        }));
        return;
      }
      setRosterEnabled((v) => !v);
    };
    window.addEventListener("crescendo:roster-toggle", onToggle);
    return () => window.removeEventListener("crescendo:roster-toggle", onToggle);
  }, [rosterAllowed]);

  // --- Apply highlight to current selection ---
  const applyHL = useCallback((color, allowToggle = false) => {
    if (!color) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    document.execCommand("styleWithCSS", false, true);
    if (allowToggle) {
      const range = sel.getRangeAt(0);
      let node = range.startContainer;
      if (node.nodeType === 3) node = node.parentNode;
      const bg = node ? getComputedStyle(node).backgroundColor : "";
      if (colorEquals(bg, color)) {
        exec("hiliteColor", "transparent");
        if (!userPickedTextColorRef?.current) exec("foreColor", "#EDEDED");
        return;
      }
    }
    if (color === "transparent") {
      exec("hiliteColor", "transparent");
      if (!userPickedTextColorRef?.current) exec("foreColor", "#EDEDED");
      return;
    }
    exec("hiliteColor", color);
    if (!userPickedTextColorRef?.current) {
      const fg = contrastFor(color);
      exec("foreColor", fg);
      if (setLastForeColor) setLastForeColor(fg);
    }
  }, [exec, userPickedTextColorRef, setLastForeColor]);

  const applyOneShot = (c) => {
    // Iter35 user request: "Clicking the highlight color (not under the
    // roster) should unhighlight the text (as in native HTML highlight)
    // and set the highlight color." Pass `allowToggle=true` so applying
    // the same color over already-highlighted text removes the highlight
    // (and changes background to transparent).
    applyHL(c, true);
    setLastOneShot(c === "transparent" ? null : c);
    if (c && c !== "transparent") { saveRecent(c); setRecents(loadRecent()); }
    setOpen(false);
  };

  // --- Roster binding from palette grid ---
  const bindSwatch = (c, button) => {
    const sameAsLeft = leftColor && colorEquals(leftColor, c);
    const sameAsRight = rightColor && colorEquals(rightColor, c);
    if (button === "left") {
      if (sameAsLeft) { setLeftColor(null); return; }
      if (sameAsRight) setRightColor(null);
      setLeftColor(c);
    } else {
      if (sameAsRight) { setRightColor(null); return; }
      if (sameAsLeft) setLeftColor(null);
      setRightColor(c);
    }
  };

  // Add a custom color to the grid (de-duped).
  const addToPalette = (c) => {
    if (!c) return null;
    const norm = toHex(c);
    if (!norm || norm === "transparent") return null;
    if (palette.some((p) => (toHex(p) || p.toLowerCase()) === norm)) return norm;
    setPalette([...palette, norm]);
    return norm;
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Live refs so editor listeners always read current state.
  const rosterEnabledRef = useRef(rosterEnabled);
  const leftColorRef = useRef(leftColor);
  const rightColorRef = useRef(rightColor);
  const paletteRef = useRef(palette);
  rosterEnabledRef.current = rosterEnabled;
  leftColorRef.current = leftColor;
  rightColorRef.current = rightColor;
  paletteRef.current = palette;

  // Editor wiring.
  useEffect(() => {
    const ed = editorRef?.current;
    if (!ed) return;

    let middleHeld = false;
    // Iter35 user request: spacebar+click was replaced by Alt+click as the
    // alternative cycle keybind (spacebar interfered with normal typing).
    let pendingButton = null;

    const cycleBinding = (which) => {
      const pal = paletteRef.current;
      if (!pal.length) return;
      const skip = which === "left" ? rightColorRef.current : leftColorRef.current;
      const cur = which === "left" ? leftColorRef.current : rightColorRef.current;
      let startIdx = 0;
      if (cur) {
        const idx = pal.findIndex((c) => colorEquals(c, cur));
        if (idx >= 0) startIdx = idx + 1;
      }
      for (let off = 0; off < pal.length; off++) {
        const c = pal[(startIdx + off) % pal.length];
        if (skip && colorEquals(c, skip)) continue;
        if (which === "left") setLeftColor(c); else setRightColor(c);
        return;
      }
    };

    const onMouseDown = (e) => {
      if (!rosterEnabledRef.current) return;
      if (e.button === 1) {
        e.preventDefault();
        middleHeld = true;
        setMiddleIndicator({ x: e.clientX, y: e.clientY });
        return;
      }
      // Alt+click cycle (iter35 spec): Alt+left-click cycles L,
      // Alt+right-click cycles R. Mirrors the middle-hold flow.
      if (e.altKey && (e.button === 0 || e.button === 2)) {
        e.preventDefault();
        cycleBinding(e.button === 0 ? "left" : "right");
        setMiddleIndicator({ x: e.clientX, y: e.clientY });
        // Auto-hide the indicator after a beat so it doesn't linger.
        setTimeout(() => setMiddleIndicator(null), 800);
        return;
      }
      if (middleHeld && (e.button === 0 || e.button === 2)) {
        e.preventDefault();
        cycleBinding(e.button === 0 ? "left" : "right");
        setMiddleIndicator({ x: e.clientX, y: e.clientY });
        return;
      }
      if (e.button === 0 && leftColorRef.current) pendingButton = 0;
      else if (e.button === 2 && rightColorRef.current) pendingButton = 2;
      else pendingButton = null;
    };

    const onMouseUp = (e) => {
      if (!rosterEnabledRef.current) { middleHeld = false; pendingButton = null; return; }
      if (e.button === 1) { middleHeld = false; setMiddleIndicator(null); return; }
      if (pendingButton !== null && e.button === pendingButton) {
        const color = pendingButton === 0 ? leftColorRef.current : rightColorRef.current;
        pendingButton = null;
        if (color) {
          requestAnimationFrame(() => {
            applyHL(color, true);
            const sel = window.getSelection();
            if (sel) { try { sel.removeAllRanges(); } catch {} }
          });
        }
      }
    };

    // Space-bar = secondary middle-button keybind. Track via window
    // keydown / keyup so the binding works even if the editor isn't
    // focused. Spec: when held inside the editor area, [space + click]
    // = [middle-click + click].
    //
    // Iter34 update: [alt + click] is now the canonical alternative
    // since spacebar interferes with normal text typing. Spacebar
    // support kept but only when selection is non-collapsed (rare).
    const onKeyDown = (e) => {
      // Iter35 fix: Alt+C must work whether the roster is currently
      // enabled OR disabled (it's a TOGGLE). Previous gate
      // `if (!rosterEnabledRef.current) return` blocked turn-on.
      if (e.altKey && (e.key === "c" || e.key === "C")) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("crescendo:roster-toggle"));
        return;
      }
      if (!rosterEnabledRef.current) return;
      // Spacebar+click cycle was removed in iter35 — Alt+click via the
      // editor mousedown handler is the canonical alt cycle binding now.
      // (Spacebar was conflicting with normal text typing.)
    };
    const onKeyUp = () => { /* no-op */ };

    const onContextMenu = (e) => { if (rosterEnabledRef.current) e.preventDefault(); };

    ed.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    ed.addEventListener("contextmenu", onContextMenu);
    return () => {
      ed.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      ed.removeEventListener("contextmenu", onContextMenu);
    };
  }, [editorRef, applyHL]);

  // Export/Import — clipboard primary, file download on clipboard fail.
  const exportRoster = () => {
    try {
      const payload = JSON.stringify({ kind: "crescendo-hl-roster", v: 2, palette, leftColor, rightColor }, null, 2);
      const blob = new Blob([payload], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crescendo-hl-roster-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${palette.length}-color roster`);
    } catch {
      toast.error("Export failed");
    }
  };

  const importRoster = () => {
    // Use a hidden file input instead of window.prompt for better UX
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,application/json";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const raw = await file.text();
        const parsed = JSON.parse(raw);
        if (parsed?.kind !== "crescendo-hl-roster") { toast.error("Invalid roster JSON."); return; }
      const incoming = Array.isArray(parsed.palette) ? parsed.palette : parsed.roster;
      if (!Array.isArray(incoming)) { toast.error("Invalid roster JSON."); return; }
      setPalette(incoming.slice(0, 24));
      if (parsed.leftColor) setLeftColor(parsed.leftColor);
      if (parsed.rightColor) setRightColor(parsed.rightColor);
      toast.success(`Imported ${incoming.length} roster colors`);
      } catch { toast.error("Couldn't parse roster JSON."); }
    };
    input.click();
  };

  const noFill = () => { applyHL("transparent", false); setLastOneShot(null); setOpen(false); };

  return (
    <div ref={wrapRef} className="relative inline-block" data-testid={tid}>
      {/* Trigger — one single highlighter button. The small color dot
          shows the currently-active highlight (roster L or last one-shot). */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={rosterEnabled ? `Roster on · L=${leftColor || "—"}  R=${rightColor || "—"}` : "Highlight"}
        className={`relative inline-flex items-center justify-center w-7 h-7 rounded border bg-[#0A0A0A] hover:border-[#5A90F5] ${rosterEnabled ? "border-[#5A90F5]" : "border-[#2A2A2A]"}`}
        data-testid={`${tid}-trigger`}
      >
        <Highlighter className="w-3.5 h-3.5 text-[#EDEDED]" />
        {(rosterEnabled ? leftColor : lastOneShot) && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-[#0A0A0A]"
            style={{ background: rosterEnabled ? leftColor : lastOneShot }}
          />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-[60] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl p-2 w-[260px]"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${tid}-popover`}
        >
          {/* Roster toggle — at top, real switch (not checkbox). */}
          <div className="flex items-center justify-between px-1 py-1.5 mb-1" data-testid={`${tid}-enable-row`}>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-[#EDEDED]">Enable Roster</span>
              <span className="text-[9px] text-[#52525B] font-mono">⌥C</span>
              {!rosterAllowed && <Lock className="w-3 h-3 text-[#A1A1AA]" />}
            </div>
            <ToggleSwitch
              checked={rosterEnabled}
              onChange={(v) => {
                if (v && !rosterAllowed) {
                  window.dispatchEvent(new CustomEvent("crescendo:paywall", {
                    detail: { error: "feature_locked", feature: "highlight_roster", plan: "standard",
                              message: "Highlight Roster is a Pro feature." },
                  }));
                  return;
                }
                setRosterEnabled(v);
              }}
              disabled={false}
              data-testid={`${tid}-enable-toggle`}
            />
          </div>
          {/* Iter35 user request: removed the "L=left-click ... middle-hold cycles"
              hint text. The keybind hint is enough — extra context just clutters. */}

          {/* Unified color grid (preset + custom). Doubles as binding grid when roster on. */}
          <div className="grid grid-cols-6 gap-1 mb-2" data-testid={`${tid}-grid`}>
            {visiblePalette.map((c) => {
              const isL = leftColor && colorEquals(leftColor, c);
              const isR = rightColor && colorEquals(rightColor, c);
              const isLast = !rosterEnabled && lastOneShot && colorEquals(lastOneShot, c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={(e) => {
                    if (rosterEnabled) { e.preventDefault(); bindSwatch(c, "left"); }
                    else applyOneShot(c);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    // Iter39: in roster mode the only context-menu
                    // action is Delete. Outside roster mode, suppress
                    // the browser's default context menu (no binding).
                    if (rosterEnabled) {
                      setColorCtx({ color: c, x: e.clientX, y: e.clientY });
                    }
                  }}
                  title={c}
                  style={{ background: c }}
                  className={`relative w-8 h-8 rounded-md border transition-all ${isL ? "ring-2 ring-[#5A90F5] border-[#5A90F5]" : isR ? "ring-2 ring-[#FF9800] border-[#FF9800]" : isLast ? "ring-2 ring-white border-white" : "border-[#2A2A2A] hover:ring-2 hover:ring-[#3A3A3A]"}`}
                  data-testid={`${tid}-swatch-${c.replace(/[#]/g, "")}`}
                />
              );
            })}

            {/* Custom color add — single popup with inline picker + Set button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setCustomHex(customHex || "#5A90F5")}
                className="w-8 h-8 rounded-md border border-dashed border-[#3A3A3A] bg-[#0A0A0A] hover:border-[#5A90F5] cursor-pointer flex items-center justify-center"
                title="Add a custom color"
                data-testid={`${tid}-add`}
              >
                <Plus className="w-3.5 h-3.5 text-[#A1A1AA]" />
              </button>
              {customHex && (
                <div className="absolute bottom-full left-0 mb-2 z-[80] bg-[#141414] border border-[#2A2A2A] rounded-lg shadow-2xl p-3 flex flex-col gap-2" style={{ minWidth: 160 }}>
                  {/* Color preview + native picker in one row */}
                  <div className="flex items-center gap-2">
                    <label className="w-8 h-8 rounded cursor-pointer border border-[#3A3A3A] overflow-hidden flex-shrink-0" style={{ background: customHex }}>
                      <input
                        type="color"
                        value={customHex}
                        onChange={(e) => setCustomHex(e.target.value)}
                        className="opacity-0 w-full h-full cursor-pointer"
                        style={{ colorScheme: "dark" }}
                      />
                    </label>
                    <input
                      type="text"
                      value={customHex}
                      onChange={(e) => { const v = e.target.value; if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setCustomHex(v); }}
                      maxLength={7}
                      className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[10px] font-mono text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                    />
                  </div>

                  {/* Set + Cancel */}
                  <div className="flex gap-1.5 pt-1 border-t border-[#2A2A2A]">
                    <button
                      type="button"
                      onClick={() => {
                        if (/^#[0-9a-fA-F]{6}$/.test(customHex)) {
                          const added = addToPalette(customHex);
                          if (!rosterEnabled && added) applyOneShot(added);
                        }
                        setCustomHex("");
                      }}
                      className="flex-1 text-[10px] px-2 py-1.5 rounded font-semibold text-white"
                      style={{ background: customHex && /^#[0-9a-fA-F]{6}$/.test(customHex) ? customHex : "#5A90F5" }}
                    >Set</button>
                    <button type="button" onClick={() => setCustomHex("")}
                      className="text-[10px] px-2 py-1.5 rounded text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a]"
                    >Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {rosterEnabled && visiblePalette.length === 0 && (
            <div className="text-[10px] text-[#52525B] text-center py-2 border border-dashed border-[#2A2A2A] rounded mb-2">
              No custom colors yet. Click <Plus className="inline w-2.5 h-2.5" /> to add one.
            </div>
          )}

          {/* Recent colors — if any, as a small condensed row. */}
          {recents.length > 0 && !rosterEnabled && (
            <div className="flex items-center gap-1 mb-2">
              <span className="text-[9px] uppercase tracking-wider text-[#A1A1AA] w-12">Recent</span>
              <div className="flex items-center gap-1">
                {recents.map((c) => (
                  <button
                    key={c}
                    onClick={() => applyOneShot(c)}
                    title={c}
                    style={{ background: c }}
                    className="w-5 h-5 rounded border border-[#2A2A2A] hover:ring-2 hover:ring-[#5A90F5]"
                  />
                ))}
              </div>
            </div>
          )}

          {rosterEnabled && (
            <div className="flex items-center gap-1 mb-2 flex-wrap">
              <button type="button" onClick={exportRoster} className="text-[10px] px-1.5 py-0.5 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:bg-[#1a1a1a] inline-flex items-center gap-1" data-testid={`${tid}-export`}>
                <Download className="w-3 h-3" /> Export
              </button>
              <button type="button" onClick={importRoster} className="text-[10px] px-1.5 py-0.5 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:bg-[#1a1a1a] inline-flex items-center gap-1" data-testid={`${tid}-import`}>
                <Upload className="w-3 h-3" /> Import
              </button>
              {isBusiness && (
                <button type="button"
                  onClick={() => setShowBrandRoster(v => !v)}
                  className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ml-auto ${showBrandRoster ? "border-[#E0B355] text-[#E0B355] bg-[#E0B355]/10" : "border-[#2A2A2A] text-[#A1A1AA] hover:bg-[#1a1a1a]"}`}
                  title="Brand roster shared with your organization"
                  data-testid={`${tid}-brand`}>
                  <Crown className="w-3 h-3" /> Brand
                </button>
              )}
            </div>
          )}
          {showBrandRoster && isBusiness && (
            <div className="mb-2 p-2 rounded border border-[#E0B355]/30 bg-[#E0B355]/5">
              <p className="text-[9px] text-[#E0B355] uppercase tracking-wider mb-1.5">Brand Roster (shared)</p>
              {brandRoster ? (
                <div className="grid grid-cols-6 gap-1 mb-1.5">
                  {(brandRoster.palette || []).map((col, i) => (
                    <button key={i} type="button"
                      style={{ background: col }}
                      onClick={() => applyOneShot(col)}
                      className="w-8 h-8 rounded-md border border-[#2A2A2A] hover:ring-2 hover:ring-white/40"
                      title={col}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-[#52525B] mb-1.5">No brand roster set.</p>
              )}
              <button type="button"
                onClick={async () => {
                  setBrandSaving(true);
                  await saveBrandRoster(apiClient || window._crescApi, { palette, leftColor, rightColor });
                  setBrandRoster({ palette, leftColor, rightColor });
                  setBrandSaving(false);
                }}
                className="text-[10px] px-2 py-0.5 rounded border border-[#E0B355]/50 text-[#E0B355] hover:bg-[#E0B355]/10 w-full">
                {brandSaving ? "Saving…" : "Push current roster as brand roster"}
              </button>
            </div>
          )}

          {/* "No Highlight Color" — bottom. */}
          <button
            type="button"
            onClick={noFill}
            className="w-full text-[11px] px-2 py-1.5 rounded border border-[#2A2A2A] bg-[#0A0A0A] text-[#EDEDED] hover:border-[#5A90F5] flex items-center justify-center gap-2"
            data-testid={`${tid}-nofill`}
          >
            <span className="w-4 h-4 rounded border border-dashed border-[#A1A1AA] inline-flex items-center justify-center text-[10px] text-[#A1A1AA]">×</span>
            No Highlight Color
          </button>

          {!rosterAllowed && (
            <div className="mt-2 flex items-center gap-1.5 px-2 py-1.5 rounded bg-[#5A90F5]/10 border border-[#5A90F5]/30 text-[10px] text-[#5A90F5]" data-testid={`${tid}-paywall-hint`}>
              <Sparkles className="w-3 h-3" />
              Roster is a Pro feature · {" "}
              <a href="/pricing" className="underline">Upgrade</a>
            </div>
          )}
        </div>
      )}

      {/* Middle-hold indicator — shows ONLY palette swatches the user has
          chosen (multi-select), not the entire preset list. Outlined
          bindings only — no L/R letter badges per iter35 user request. */}
      {middleIndicator && (
        <div
          className="fixed z-[200] pointer-events-none"
          style={{ left: middleIndicator.x + 16, top: middleIndicator.y + 16 }}
          data-testid={`${tid}-middle-indicator`}
        >
          <div className="bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl px-2 py-1.5 flex items-center gap-1.5">
            {palette.length === 0 ? (
              <span className="text-[10px] text-[#52525B]">Empty palette</span>
            ) : (
              palette.map((c) => {
                const isL = leftColor && colorEquals(leftColor, c);
                const isR = rightColor && colorEquals(rightColor, c);
                return (
                  <span
                    key={c}
                    title={c}
                    className={`relative w-6 h-6 rounded-md border-2 ${isL ? "border-[#5A90F5]" : isR ? "border-[#FF9800]" : "border-[#2A2A2A]"}`}
                    style={{ background: c }}
                  />
                );
              })
            )}
          </div>
        </div>
      )}
      {/* Iter39: right-click context menu on a roster swatch — only
          shows the red Delete button. Outside roster mode this menu
          never opens. */}
      {colorCtx && (
        <>
          <div className="fixed inset-0 z-[210]" onClick={() => setColorCtx(null)} onContextMenu={(e) => { e.preventDefault(); setColorCtx(null); }} />
          <div
            className="fixed z-[211] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl p-1 min-w-[140px]"
            style={{ left: Math.min(colorCtx.x, window.innerWidth - 160), top: Math.min(colorCtx.y, window.innerHeight - 60) }}
            data-testid={`${tid}-color-ctx`}
          >
            <div className="px-2 py-1 flex items-center gap-1.5 border-b border-[#2A2A2A]">
              <span className="w-3 h-3 rounded border border-[#2A2A2A]" style={{ background: colorCtx.color }} />
              <span className="font-mono text-[10px] text-[#A1A1AA]">{colorCtx.color}</span>
            </div>
            <button
              type="button"
              onClick={() => removeFromPalette(colorCtx.color)}
              className="w-full text-left px-2 py-1.5 rounded text-[11px] text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1.5"
              data-testid={`${tid}-color-ctx-delete`}
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}
